import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  LinearProgress,
  Chip,
  Stack,
  CircularProgress
} from '@mui/material';
import { CheckCircle, Schedule, DataUsage } from '@mui/icons-material';

const GlobalLoadingIndicator = ({ 
  open, 
  loadingStates = {}, 
  onClose = () => {} 
}) => {
  const contexts = [
    { key: 'technicians', label: 'Techniciens', icon: '👥' },
    { key: 'skills', label: 'Compétences', icon: '🛠️' },
    { key: 'oncalls', label: 'Astreintes', icon: '📅' },
    { key: 'absences', label: 'Absences', icon: '🏖️' }
  ];

  const getContextStatus = (key) => {
    const state = loadingStates[key];
    if (state?.loaded) return 'completed';
    if (state?.loading) return 'loading';
    return 'pending';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'loading':
        return <CircularProgress size={20} />;
      default:
        return <Schedule sx={{ color: 'text.disabled', fontSize: 20 }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'loading': return 'primary';
      default: return 'default';
    }
  };

  const completedCount = contexts.filter(ctx => getContextStatus(ctx.key) === 'completed').length;
  const totalCount = contexts.length;
  const progressPercent = (completedCount / totalCount) * 100;

  const isLoading = contexts.some(ctx => getContextStatus(ctx.key) === 'loading');
  const allCompleted = completedCount === totalCount;

  // Auto-fermeture après 1 seconde quand tout est chargé
  React.useEffect(() => {
    if (allCompleted && open) {
      const timer = setTimeout(() => {
        onClose();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [allCompleted, open, onClose]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {}} // Empêche la fermeture manuelle
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1
        }
      }}
    >
      <DialogContent sx={{ textAlign: 'center', py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <DataUsage sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Chargement des données
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Merci de patienter pendant la synchronisation...
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              Progression : {completedCount}/{totalCount}
            </Typography>
            <Typography variant="body2" color="primary.main" fontWeight="bold">
              {Math.round(progressPercent)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progressPercent}
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4
              }
            }}
          />
        </Box>

        <Stack spacing={2}>
          {contexts.map((context) => {
            const status = getContextStatus(context.key);
            const state = loadingStates[context.key];
            
            return (
              <Box
                key={context.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: status === 'completed' ? 'success.50' : 
                                   status === 'loading' ? 'primary.50' : 'grey.50',
                  border: 1,
                  borderColor: status === 'completed' ? 'success.200' : 
                              status === 'loading' ? 'primary.200' : 'grey.200'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography fontSize="1.2em">
                    {context.icon}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {context.label}
                  </Typography>
                  {state?.count && (
                    <Typography variant="caption" color="text.secondary">
                      ({state.count} éléments)
                    </Typography>
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {status === 'loading' && state?.progress && (
                    <Typography variant="caption" color="primary.main">
                      {state.progress}
                    </Typography>
                  )}
                  <Chip
                    icon={getStatusIcon(status)}
                    label={
                      status === 'completed' ? 'Terminé' :
                      status === 'loading' ? 'En cours...' : 'En attente'
                    }
                    size="small"
                    color={getStatusColor(status)}
                    variant={status === 'completed' ? 'filled' : 'outlined'}
                  />
                </Box>
              </Box>
            );
          })}
        </Stack>

        {allCompleted && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="success.main" fontWeight="bold">
              ✅ Chargement terminé ! Redirection automatique...
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GlobalLoadingIndicator;